import React from 'react';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import { DEFAULT_PAGE_SIZES } from '../../hooks/usePaginatedQuery';

// PaginatedTable — shared admin list-page surface. Wraps an existing render
// function (the caller's columns/rows logic) and adds a consistent toolbar:
// page size dropdown, "Showing X–Y of Z", first/prev/next/last buttons.
//
// Accepts a paginator object from usePaginatedQuery (or a similar shape):
//   { rows, total, loading, error, page, pageCount, limit, setLimit, setOffset }
//
// renderTable(rows) is the caller's existing table — we don't impose a
// specific column shape because every list page renders rows differently
// (DataTable, custom grid, kanban-row, etc.). The component supplies the
// pagination chrome around whatever the caller renders.
//
// Empty states: caller can pass `emptyMessage` (no filters) and
// `emptyFilteredMessage` (one or more filters set). When `anyFilterSet` is
// true, the filtered message renders.

export default function PaginatedTable({
  paginator,
  renderTable,
  loadingSkeletonRows = 5,
  emptyMessage = 'No records yet.',
  emptyFilteredMessage = 'No records match your filters.',
  anyFilterSet = false,
  toolbarLeft = null,
  toolbarRight = null,
  pageSizes = DEFAULT_PAGE_SIZES,
  showPager = true,
  className = '',
}) {
  const {
    rows, total, loading, error,
    page, pageCount, limit, offset,
    setLimit, setOffset,
  } = paginator;

  const startIdx = total === 0 ? 0 : offset + 1;
  const endIdx = Math.min(offset + (rows?.length ?? 0), total);

  const onPageSizeChange = (e) => {
    const n = parseInt(e.target.value, 10);
    if (Number.isFinite(n)) setLimit(n);
  };

  const goFirst = () => setOffset(0);
  const goPrev = () => setOffset(Math.max(offset - limit, 0));
  const goNext = () => setOffset(offset + limit);
  const goLast = () => setOffset((pageCount - 1) * limit);

  const isFirst = page === 0;
  const isLast = page >= pageCount - 1;

  return (
    <div className={`space-y-3 ${className}`} data-testid="paginated-table">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3 text-[12px] text-gray-400">
        {toolbarLeft}
        <div className="flex items-center gap-1.5">
          <label htmlFor="paginated-page-size" className="text-gray-500">Show</label>
          <select
            id="paginated-page-size"
            value={limit}
            onChange={onPageSizeChange}
            className="bg-white/[0.03] border border-white/[0.08] rounded-lg px-2 py-1 text-[12px] text-white"
            data-testid="paginated-page-size"
          >
            {pageSizes.map((n) => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
          <span className="text-gray-500">per page</span>
        </div>
        <div className="text-gray-500" data-testid="paginated-count">
          {loading && total === 0
            ? 'Loading…'
            : total === 0
              ? '0 of 0'
              : `Showing ${startIdx}–${endIdx} of ${total}`}
        </div>
        <div className="ml-auto flex items-center gap-2">
          {toolbarRight}
        </div>
      </div>

      {/* Body */}
      {error && (
        <div className="text-[12px] text-red-300 bg-red-500/10 border border-red-500/20 rounded-lg p-3">
          {error}
        </div>
      )}
      {loading && rows.length === 0 ? (
        <PaginatedSkeleton rows={loadingSkeletonRows} />
      ) : !loading && rows.length === 0 ? (
        <div
          className="rounded-xl border border-white/[0.05] bg-white/[0.01] py-12 text-center text-[13px] text-gray-500"
          data-testid="paginated-empty"
        >
          {anyFilterSet ? emptyFilteredMessage : emptyMessage}
        </div>
      ) : (
        renderTable(rows)
      )}

      {/* Pager */}
      {showPager && total > limit && (
        <div className="flex items-center justify-between gap-3 pt-1" data-testid="paginated-pager">
          <PageButton onClick={goFirst} disabled={isFirst} aria-label="First page">
            <ChevronsLeft className="w-3.5 h-3.5" />
          </PageButton>
          <PageButton onClick={goPrev} disabled={isFirst} aria-label="Previous page">
            <ChevronLeft className="w-3.5 h-3.5" />
            Prev
          </PageButton>
          <div className="text-[12px] text-gray-500" data-testid="paginated-pager-position">
            Page {page + 1} of {pageCount}
          </div>
          <PageButton onClick={goNext} disabled={isLast} aria-label="Next page">
            Next
            <ChevronRight className="w-3.5 h-3.5" />
          </PageButton>
          <PageButton onClick={goLast} disabled={isLast} aria-label="Last page">
            <ChevronsRight className="w-3.5 h-3.5" />
          </PageButton>
        </div>
      )}
    </div>
  );
}

function PageButton({ children, ...rest }) {
  return (
    <button
      type="button"
      className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-white/[0.08] text-[12px] text-gray-300 hover:text-white hover:border-white/[0.18] disabled:opacity-40 disabled:cursor-not-allowed"
      {...rest}
    >
      {children}
    </button>
  );
}

function PaginatedSkeleton({ rows = 5 }) {
  return (
    <div className="space-y-2" data-testid="paginated-skeleton">
      {Array.from({ length: rows }).map((_, i) => (
        <div
          key={i}
          className="h-12 rounded-lg bg-white/[0.025] animate-pulse"
        />
      ))}
    </div>
  );
}
